import React, { useEffect, useRef, useState } from "react";
import { FaBookmark, FaPlus, FaTimes } from "react-icons/fa";
import { useQueryClient } from "@tanstack/react-query";
import { useCollectionsQuery } from "../../hooks/profile/queries/useCollectionsQuery";
import api from "../../utils/api/axiosInstance";
import getImageUrl from "../../utils/imageUtils";
import styles from "../../styles/components/profile/SaveCollectionModal.module.css";

/**
 * Modal that lets a user pick (or create) a collection when saving a post.
 *
 * Props:
 *   userId       - current user ID (for query key)
 *   onSave       - fn(collectionName: string) called when a collection is chosen
 *   onClose      - fn() to close the modal
 */
const SaveCollectionModal = ({ userId, onSave, onClose }) => {
  const queryClient = useQueryClient();
  const { data: collections = [], isLoading } = useCollectionsQuery(userId, !!userId);

  /* inline new-collection form */
  const [showNew, setShowNew] = useState(false);
  const [newName, setNewName] = useState("");
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState("");
  const newInputRef = useRef(null);

  useEffect(() => {
    if (showNew) {
      setNewName("");
      setCreateError("");
      setTimeout(() => newInputRef.current?.focus(), 50);
    }
  }, [showNew]);

  const handleCreateAndSave = async (e) => {
    e.preventDefault();
    const trimmed = newName.trim();
    if (!trimmed) { setCreateError("Please enter a name."); return; }
    if (trimmed.length > 60) { setCreateError("Max 60 characters."); return; }
    setCreating(true);
    setCreateError("");
    try {
      await api.post("/posts/collections", { name: trimmed });
      queryClient.invalidateQueries(["savedCollections", userId]);
      onSave(trimmed);
    } catch (err) {
      setCreateError(err?.response?.data?.message || "Could not create collection.");
      setCreating(false);
    }
  };

  /* close on Escape */
  useEffect(() => {
    const handler = (e) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, [onClose]);

  return (
    <div className={styles.overlay} onClick={onClose}>
      <div className={styles.modal} onClick={(e) => e.stopPropagation()}>

        <div className={styles.header}>
          <span className={styles.headerTitle}>
            <FaBookmark size={14} /> Save to collection
          </span>
          <button type="button" className={styles.closeBtn} onClick={onClose} aria-label="Close">
            <FaTimes />
          </button>
        </div>

        <div className={styles.body}>
          {isLoading ? (
            <p className={styles.hint}>Loading collections...</p>
          ) : (
            <ul className={styles.list}>
              {collections.map((col) => (
                <li key={col.name}>
                  <button
                    type="button"
                    className={styles.colItem}
                    onClick={() => onSave(col.name)}
                  >
                    <div className={styles.colThumb}>
                      {col.coverImage ? (
                        <img src={getImageUrl(col.coverImage)} alt={col.name} className={styles.colThumbImg} />
                      ) : (
                        <FaBookmark size={16} className={styles.colThumbIcon} />
                      )}
                    </div>
                    <div className={styles.colInfo}>
                      <span className={styles.colName}>{col.name}</span>
                      <span className={styles.colCount}>
                        {col.postCount} post{col.postCount === 1 ? "" : "s"}
                      </span>
                    </div>
                  </button>
                </li>
              ))}
            </ul>
          )}

          {/* New collection inline form */}
          {showNew ? (
            <form className={styles.newForm} onSubmit={handleCreateAndSave}>
              <input
                ref={newInputRef}
                type="text"
                className={styles.newInput}
                placeholder="Collection name"
                value={newName}
                onChange={(e) => { setNewName(e.target.value); setCreateError(""); }}
                maxLength={60}
              />
              {createError && <p className={styles.createError}>{createError}</p>}
              <div className={styles.newFormActions}>
                <button
                  type="button"
                  className={styles.cancelNewBtn}
                  onClick={() => setShowNew(false)}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className={styles.createBtn}
                  disabled={creating || !newName.trim()}
                >
                  {creating ? "Creating..." : "Create & Save"}
                </button>
              </div>
            </form>
          ) : (
            <button
              type="button"
              className={styles.newCollectionBtn}
              onClick={() => setShowNew(true)}
            >
              <FaPlus size={12} /> New Collection
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default SaveCollectionModal;
